import { ReactNode } from 'react';
import { ChevronRight } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { MollieConnection, MollieProfile } from '../../Shared/types';
import { LinkButton } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { Icon } from '../../Shared/UI/Icon';
import { useRouter } from '../router';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';
import MollieConnectionCard from './MollieConnectionCard';
import MollieProfileForm from './MollieProfileForm';
import PaymentReferenceForm from './PaymentReferenceForm';

export interface MolliePageProps extends SharedProps {
  mollieConnections: MollieConnection[];
}

export default function MolliePage({
  mollieConnections,
  charity,
}: MolliePageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();

  const mollieProfiles = mollieConnections.reduce((mollieProfiles, mollieConnection) => ([
    ...mollieProfiles,
    ...mollieConnection.profiles,
  ]), [] as MollieProfile[]);

  return (
    <>
      <Header>
        <Container size="md">
          <HeaderTitle>
            {t('backend:payment_settings')}
          </HeaderTitle>
          {charity.can_receive_payments && (
            <div className="font-medium text-emerald-600">
              {t('backend:ready_to_receive_donations')}
            </div>
          )}
          {!charity.can_receive_payments && (
            <div className="font-medium text-red-600">
              {t('backend:cannot_receive_donations_yet')}
            </div>
          )}
        </Container>
      </Header>
      {mollieConnections.length === 0 && (
        <Container size="md" className="space-y-6">
          <Card>
            <h2 className="mb-1">
              {t('backend:connect_with_mollie')}
            </h2>
            <p className="mb-4">
              {t('backend:connect_with_mollie_description')}
            </p>
            <LinkButton href={routes.mollie_authorization()} className="font-bold">
              {t('backend:connect_with_mollie')}
              <Icon className="ml-1">
                <ChevronRight />
              </Icon>
            </LinkButton>
          </Card>
        </Container>
      )}
      {mollieConnections.length > 0 && (
        <Container size="md" className="space-y-6">
          {mollieProfiles.length > 0 && (
            <div className="space-y-4">
              <h2>
                {t('backend:website_profile')}
              </h2>
              <MollieProfileForm mollieConnections={mollieConnections} />
            </div>
          )}

          <hr className="border-slate-200" />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2>
                {t('backend:your_mollie_accounts', { count: mollieConnections.length })}
              </h2>
              <LinkButton href={routes.mollie_authorization()} variant="secondary">
                {t('backend:add_mollie_account')}
              </LinkButton>
            </div>
            {mollieConnections.map((connection) => (
              <MollieConnectionCard connection={connection} key={connection.id} />
            ))}
          </div>

          <hr className="border-slate-200" />

          <div className="space-y-4">
            <h2>
              {t('backend:payment_reference')}
            </h2>
            <PaymentReferenceForm paymentReference={charity.payment_reference} />
          </div>
        </Container>
      )}
    </>
  );
}

MolliePage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
